import { Suspense, Component, ReactNode } from 'react';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  retryCount: number;
}

const MAX_SOFT_RETRIES = 2;

function isDomMutationError(error: Error): boolean {
  const msg = error.message || '';
  return msg.includes('insertBefore') ||
    msg.includes('removeChild') ||
    msg.includes('is not a child of this node');
}

class LazyErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false, retryCount: 0 };

  static getDerivedStateFromError(): Partial<ErrorBoundaryState> {
    return { hasError: true };
  }

  componentDidCatch(error: Error): void {
    if (isDomMutationError(error) && this.state.retryCount < MAX_SOFT_RETRIES) {
      console.warn('Google Translate DOM conflict detected, retrying render...', error);
      this.setState(prev => ({ hasError: false, retryCount: prev.retryCount + 1 }));
    } else {
      console.error('Lazy load failed:', error);
    }
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, retryCount: 0 });
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '200px',
          padding: '40px',
          textAlign: 'center'
        }}>
          <h3 style={{ marginBottom: '16px', color: '#1D2129' }}>
            <span>Failed to load page</span>
          </h3>
          <p style={{ marginBottom: '24px', color: '#86909C' }}>
            <span>Please check your connection and try again.</span>
          </p>
          <button
            onClick={this.handleRetry}
            style={{
              padding: '12px 24px',
              fontSize: '16px',
              fontWeight: 600,
              color: '#fff',
              backgroundColor: '#0066FF',
              border: 'none',
              borderRadius: '50px',
              cursor: 'pointer'
            }}
          >
            <span>Refresh Page</span>
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export const Lazy = ({ children }: { children: ReactNode }): JSX.Element => (
  <LazyErrorBoundary>
    <Suspense fallback={<LoadingSpinner />}>
      {children}
    </Suspense>
  </LazyErrorBoundary>
);

export default Lazy;
